import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as TaskActions from './tasks.actions';
import { GetTasksDto, ITask } from '@appgen-health-crm/api-interfaces';

export const TASK_FEATURE_KEY = 'task';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface TaskEntity extends ITask {}

export interface State extends EntityState<TaskEntity> {
  loading: boolean;
  filters: GetTasksDto;
  activeTask: TaskEntity;
  createTask: {
    open: boolean;
    loading: boolean;
  };
  taskDetails: {
    open: boolean;
    loading: boolean;
  };
  updatedAt?: Date;
  error?: any;
  hasMore: boolean;
}

export interface TaskPartialState {
  readonly [TASK_FEATURE_KEY]: State;
}

export function selectId(Task: any): string {
  return Task._id;
}

export const TaskAdapter: EntityAdapter<TaskEntity> =
  createEntityAdapter<TaskEntity>({ selectId });

export const initialState: State = TaskAdapter.getInitialState({
  loading: false,
  activeTask: null,
  createTask: {
    open: false,
    loading: false
  },
  taskDetails: {
    open: false,
    loading: false
  },
  filters: {
    limit: 25,
    offset: 0
  },
  hasMore: false
});

const TaskReducer = createReducer(
  initialState,

  /* -------------------------------- Get Tasks ------------------------------- */

  on(TaskActions.getTasks, (state, { filters }) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(TaskActions.getTasksSuccess, (state, { tasksResp, loadType }) => {
    if (loadType === 'older') {
      return TaskAdapter.addMany(tasksResp.data, {
        ...state,
        loading: false,
        updatedAt: new Date(),
        filters: {
          ...state.filters,
          offset: state.filters.offset + state.filters.limit
        },
        hasMore: tasksResp.hasMore
      });
    }

    return TaskAdapter.setAll(tasksResp.data, {
      ...state,
      loading: false,
      updatedAt: new Date(),
      filters: {
        ...state.filters,
        offset: 0
      },
      hasMore: tasksResp.hasMore
    });
  }),
  on(TaskActions.getTasksFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  /* ----------------------------- Get Task By Id ----------------------------- */

  on(TaskActions.getTaskById, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(TaskActions.getTaskByIdSuccess, (state, { task }) => ({
    ...state,
    loading: false,
    error: null,
    activeTask: task
  })),
  on(TaskActions.getTaskByIdFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
    activeTask: null
  })),

  /* ------------------------------ Task details ------------------------------ */
  // open
  on(TaskActions.openTaskDetails, state => ({
    ...state,
    taskDetails: { ...state.taskDetails, open: true }
  })),
  // close
  on(TaskActions.closeTaskDetails, state => ({
    ...state,
    taskDetails: { ...state.taskDetails, open: false }
  })),

  /* ---------------------------- Create CRM user --------------------------- */

  on(TaskActions.createTask, (state, { request }) => ({
    ...state,
    createTask: { ...state.createTask, loading: true },
    error: null
  })),
  // success
  on(TaskActions.createTaskSuccess, (state, { task }) => ({
    ...state,
    createTask: { ...state.createTask, loading: false },
    error: null
  })),
  // failure
  on(TaskActions.createTaskFailure, (state, { error }) => ({
    ...state,
    createTask: { ...state.createTask, loading: false },
    error
  })),
  // open
  on(TaskActions.createTaskFailure, state => ({
    ...state,
    createTask: { ...state.createTask, open: true }
  })),
  // close
  on(TaskActions.createTaskFailure, state => ({
    ...state,
    createTask: { ...state.createTask, open: false }
  })),

  on(TaskActions.updateTask, (state, { task }) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(TaskActions.updateTaskSuccess, (state, { task }) =>
    TaskAdapter.updateOne(
      {
        id: task._id,
        changes: { ...task }
      },
      {
        ...state,
        loading: false,
        error: null
      }
    )
  ),
  on(TaskActions.updateTaskFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  /* ------------------------------- Delete Task ------------------------------ */

  on(TaskActions.deleteTask, (state, { id }) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(TaskActions.deleteTaskSuccess, (state, { id }) =>
    TaskAdapter.removeOne(id, {
      ...state,
      loading: false,
      error: null
    })
  ),
  on(TaskActions.deleteTaskFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return TaskReducer(state, action);
}
